/**
 * Saxophone type
 */

export function getSaxophoneTypes() {
  return ["soprano", "alto", "tenor", "baritone", "bass"];
}

export function isDisabledSaxophoneType(saxophoneType) {
  const disabledSaxophoneTypes = [];
  return !!~disabledSaxophoneTypes.indexOf(saxophoneType);
}

export function isValidSaxophoneType(saxophoneType) {
  const options = getSaxophoneTypes();
  return (
    !!~options.indexOf(saxophoneType) && !isDisabledSaxophoneType(saxophoneType)
  );
}

/**
 * Baffle
 */

export function getBaffleTypes(saxophoneType) {
  switch (saxophoneType) {
    case "soprano":
      return ["B", "A", "P3", "ZB", "Su", "D"];

    case "alto":
      return ["B", "A", "P3", "ZB", "D", "U", "U2", "Es", "P6"];

    case "tenor":
      return ["B", "A", "P5", "ZB", "D"];

    case "baritone":
    case "bass":
      return ["B", "A", "P5", "ZB", "D", "K", "T"];

    default:
      return [];
  }
}

export function isDisabledBaffleType(baffleType, saxophoneType) {
  let disabledBaffleTypes = [];

  switch (saxophoneType) {
    case "soprano":
      disabledBaffleTypes = [];
      break;

    case "alto":
      disabledBaffleTypes = [];
      break;

    case "tenor":
      disabledBaffleTypes = [];
      break;

    case "baritone":
    case "bass":
      disabledBaffleTypes = [];
      break;

    default:
      return true;
  }

  return !!~disabledBaffleTypes.indexOf(baffleType);
}

export function isValidBaffleType(baffleType, saxophoneType) {
  const options = getBaffleTypes(saxophoneType);
  return (
    !!~options.indexOf(baffleType) &&
    !isDisabledBaffleType(baffleType, saxophoneType)
  );
}

export function isValidBaffleParam(baffleParam) {
  return parseFloat(baffleParam) === baffleParam;
}

export function isValidBaffleParams(baffleParams, baffleType, saxophoneType) {
  if (!Array.isArray(baffleParams)) return false;
  const requiredParamsLength = getDefaultBaffleParams(
    baffleType,
    saxophoneType
  ).length;

  if (requiredParamsLength !== baffleParams.length) return false;

  switch (baffleType) {
    default:
      return !baffleParams.find((param) => !isValidBaffleParam(param));
  }
}

export function getDefaultBaffleParams(baffleType, saxophoneType) {
  switch (baffleType) {
    case "B":
    case "K":
    case "T":
      return [0, 0];

    case "A":
    case "P3":
    case "Su":
    case "U":
    case "U2":
    case "Es":
    case "P6":
    case "P5":
    case "ZB":
      return [0];

    case "D":
      switch (saxophoneType) {
        case "soprano":
          return [];

        default:
          return [0];
      }

    default:
      return [];
  }
}

/**
 * Chamber
 */

export function getChamberTypes(saxophoneType) {
  switch (saxophoneType) {
    case "soprano":
      return ["-", "Sp"];

    case "alto":
      return ["-", "Ch", "C"];

    case "tenor":
      return ["-", "Ch"];

    case "baritone":
    case "bass":
      return ["-"];

    default:
      return [];
  }
}

export function isDisabledChamberType(chamberType, saxophoneType) {
  let disabledChamberTypes = [];

  switch (saxophoneType) {
    case "soprano":
      disabledChamberTypes = ["Sp"];
      break;

    case "alto":
      disabledChamberTypes = ["Ch", "C"];
      break;

    case "tenor":
      disabledChamberTypes = ["Ch"];
      break;

    case "baritone":
    case "bass":
      disabledChamberTypes = [];
      break;

    default:
      return true;
  }

  return !!~disabledChamberTypes.indexOf(chamberType);
}

export function isValidChamberType(chamberType, saxophoneType) {
  const options = getChamberTypes(saxophoneType);
  return (
    !!~options.indexOf(chamberType) &&
    !isDisabledChamberType(chamberType, saxophoneType)
  );
}

export function getDefaultChamberType() {
  return "-";
}

export function getChamberSizeNames() {
  return ["XN", "N", "XS", "S", "M", "L", "XL", "G", "XG"];
}

export function isValidChamberSizeName(chamberSizeName) {
  const options = getChamberSizeNames();
  return !!~options.indexOf(chamberSizeName);
}

export function isValidChamberModifier(chamberModifier) {
  return (
    parseInt(chamberModifier, 10) === chamberModifier && chamberModifier >= 0
  );
}

export function getDefaultChamberModifier() {
  return 0;
}

/**
 * Striations
 */

export function isValidStriations(baffleStriations, baffleType, saxophoneType) {
  if (parseInt(baffleStriations, 10) !== baffleStriations) {
    return false;
  }

  if (saxophoneType === "soprano") {
    return baffleStriations === 0;
  }

  if (saxophoneType === "alto" && baffleType === "Es") {
    return baffleStriations === 0;
  }

  return baffleStriations >= 0;
}

export function getDefaultStriations() {
  return 0;
}

/**
 * Tip opening
 */

export function getTipOpeningRange(saxophoneType) {
  switch (saxophoneType) {
    case "soprano":
      return [110, 260];

    case "alto":
      return [140, 380];

    case "tenor":
      return [120, 430];

    case "baritone":
    case "bass":
      return [190, 430];

    default:
      return [0, Infinity];
  }
}

export function isValidTipOpening(tipOpening, saxophoneType) {
  const range = getTipOpeningRange(saxophoneType);
  const min = range[0];
  const max = range[1];

  return (
    parseInt(tipOpening, 10) === tipOpening &&
    tipOpening >= min &&
    tipOpening <= max
  );
}

/**
 * Facing
 */

export function isValidFacing(facing) {
  return parseInt(facing, 10) === facing && facing > 0;
}

export function getDefaultFacing(saxophoneType) {
  // prettier-ignore
  switch (saxophoneType) {
    case "soprano":  return 22;
    case "alto":     return 24;
    case "tenor":    return 26;
    case "baritone": return 27;
    case "bass":     return 27;

    default:
      return null;
  }
}

/**
 * Bore diameter
 */

export function isValidBoreDiameter(boreDiameter) {
  return parseFloat(boreDiameter) === boreDiameter && boreDiameter > 0;
}

export function getDefaultBoreDiameter(saxophoneType) {
  // prettier-ignore
  switch (saxophoneType) {
    case "soprano":  return 13.65;
    case "alto":     return 16.1;
    case "tenor":    return 17.2;
    case "baritone": return 18;
    case "bass":     return 18;

    default:
      return null;
  }
}

/**
 * Internal volume
 */

export function isValidInternalVolume(internalVolume) {
  return parseFloat(internalVolume) === internalVolume && internalVolume > 0;
}

export function getDefaultInternalVolume(saxophoneType) {
  // prettier-ignore
  switch (saxophoneType) {
    case "soprano":  return 7;
    case "alto":     return 12.75;
    case "tenor":    return 18.2;
    case "baritone": return 25;
    case "bass":     return 25;

    default:
      return null;
  }
}

/**
 * Mouthpiece
 */

export function getMouthpieceErrors(mouthpiece = {}) {
  const saxophoneType = mouthpiece.saxophoneType;
  const baffle = mouthpiece.baffle || {};
  const chamber = mouthpiece.chamber || {};
  const striations = mouthpiece.striations;
  const tipOpening = mouthpiece.tipOpening;
  const facing = mouthpiece.facing;
  const boreDiameter = mouthpiece.boreDiameter;
  const internalVolume = mouthpiece.internalVolume;

  const errors = {};

  if (!isValidSaxophoneType(saxophoneType))
    errors["saxophoneType"] = `Invalid saxophone type: ${saxophoneType}`;

  if (!isValidBaffleType(baffle.type, saxophoneType))
    errors["baffle.type"] = `Invalid baffle type: ${baffle.type}`;

  if (!isValidBaffleParams(baffle.params, baffle.type, saxophoneType))
    errors["baffle.params"] = `Invalid baffle params: ${baffle.params}`;

  if (!isValidChamberType(chamber.type, saxophoneType))
    errors["chamber.type"] = `Invalid chamber type: ${chamber.type}`;

  if (!isValidChamberSizeName(chamber.sizeName))
    errors[
      "chamber.sizeName"
    ] = `Invalid chamber size name: ${chamber.sizeName}`;

  if (!isValidChamberModifier(chamber.modifier))
    errors[
      "chamber.modifier"
    ] = `Invalid chamber modifier: ${chamber.modifier}`;

  if (!isValidStriations(striations, baffle.type, saxophoneType))
    errors["striations"] = `Invalid striations: ${striations}`;

  if (!isValidTipOpening(tipOpening, saxophoneType))
    errors["tipOpening"] = `Invalid tip opening: ${tipOpening}`;

  if (!isValidFacing(facing)) errors["facing"] = `Invalid facing: ${facing}`;

  if (!isValidInternalVolume(internalVolume))
    errors["internalVolume"] = `Invalid internal volume: ${internalVolume}`;

  if (!isValidBoreDiameter(boreDiameter))
    errors["boreDiameter"] = `Invalid internal volume: ${boreDiameter}`;

  return Object.keys(errors).length ? errors : undefined;
}

function validateMouthpiece(mouthpiece) {
  const errors = getMouthpieceErrors(mouthpiece);
  if (!errors) return;

  const message = Object.values(errors).join("\n");
  throw new Error(message);
}

export function isValidMouthpiece(mouthpiece) {
  try {
    validateMouthpiece(mouthpiece);
    return true;
  } catch (err) {
    return false;
  }
}

export function applyMouthpieceDefaults(mouthpiece = {}) {
  const saxophoneType = mouthpiece.saxophoneType;

  if (!isValidSaxophoneType(saxophoneType)) return mouthpiece;

  const result = Object.assign({}, mouthpiece);

  result.baffle = result.baffle || {};
  result.chamber = result.chamber || {};

  // Baffle params
  result.baffle.params =
    result.baffle.params != null
      ? result.baffle.params
      : getDefaultBaffleParams(result.baffle.type, saxophoneType);

  // Chamber type
  result.chamber.type =
    result.chamber.type != null ? result.chamber.type : getDefaultChamberType();

  // Chamber modifier
  result.chamber.modifier =
    result.chamber.modifier != null
      ? result.chamber.modifier
      : getDefaultChamberModifier();

  // Striations
  result.striations =
    result.striations != null ? result.striations : getDefaultStriations();

  // Facing
  result.facing =
    result.facing != null ? result.facing : getDefaultFacing(saxophoneType);

  // Internal volume
  let defaultInternalVolume = getDefaultInternalVolume(saxophoneType);
  if (saxophoneType === "bass") defaultInternalVolume += 4;

  result.internalVolume =
    result.internalVolume != null
      ? result.internalVolume
      : defaultInternalVolume;

  // Bore diameter
  result.boreDiameter =
    result.boreDiameter != null
      ? result.boreDiameter
      : getDefaultBoreDiameter(saxophoneType);

  return result;
}

// prettier-ignore
export const CHAMBER_SIZES_CONFIG = {
  soprano:  { XN: 8,  N: 9,  XS: 10, S: 11, M: 12, L: 13, XL: 14, G: 15, XG: 16 },
  alto:     { XN: 10, N: 11, XS: 12, S: 13, M: 14, L: 15, XL: 16, G: 17, XG: 18 },
  tenor:    { XN: 13, N: 14, XS: 15, S: 16, M: 17, L: 18, XL: 19, G: 20, XG: 21 },
  baritone: { XN: 13, N: 14, XS: 15, S: 16, M: 17, L: 18, XL: 19, G: 20, XG: 21 },
  bass:     { XN: 13, N: 14, XS: 15, S: 16, M: 17, L: 18, XL: 19, G: 20, XG: 21 },
};

export function getChamberSize(mouthpiece) {
  validateMouthpiece(mouthpiece);

  const saxophoneType = mouthpiece.saxophoneType;
  const chamber = mouthpiece.chamber || {};

  let baseValue = CHAMBER_SIZES_CONFIG[saxophoneType][chamber.sizeName];

  return parseFloat(baseValue + "." + mouthpiece.chamber.modifier);
}

export function getNomenclature(mouthpiece) {
  validateMouthpiece(mouthpiece);

  const saxophoneType = mouthpiece.saxophoneType;
  const baffle = mouthpiece.baffle || {};
  const chamber = mouthpiece.chamber || {};
  const striations = mouthpiece.striations;
  const tipOpening = mouthpiece.tipOpening;
  const facing = mouthpiece.facing;
  const boreDiameter = mouthpiece.boreDiameter;
  const internalVolume = mouthpiece.internalVolume;

  // Baffle

  const formatBaffleParam = (withSign) => (param) => {
    const sign = Math.sign(param) > 0 ? "+" : "-";

    return param === 0
      ? ""
      : (withSign || sign === "-" ? sign : "") + Math.abs(param);
  };

  const baffleParams = baffle.params.filter((param) => param !== 0);
  const baffleResult =
    baffle.type +
    baffleParams.map(formatBaffleParam(baffleParams.length < 2)).join("&");

  // Chamber

  const chamberResult = [
    chamber.type === "-" ? "" : chamber.type,
    chamber.sizeName,
    chamber.modifier === 0 ? "" : chamber.modifier,
  ].join("");

  // Striations

  const striationsResult = striations === 0 ? "" : `x${striations}`;

  // Internal volume

  const defaultVolume = getDefaultInternalVolume(saxophoneType);
  const internalVolumeModifier = internalVolume - defaultVolume;
  const internalVolumeSign = Math.sign(internalVolumeModifier) > 0 ? "+" : "-";
  const internalVolumeResult =
    internalVolumeModifier === 0
      ? ""
      : ` V${internalVolumeSign}${Math.abs(internalVolumeModifier)
          .toFixed(3)
          .replace(/0*$/, "")}`;

  // Bore diameter

  const defaultBoreDiameter = getDefaultBoreDiameter(saxophoneType);
  const boreDiameterModifier = boreDiameter - defaultBoreDiameter;
  const shankdiameterSign = Math.sign(boreDiameterModifier) > 0 ? "+" : "-";
  const boreDiameterResult =
    boreDiameterModifier === 0
      ? ""
      : ` D${shankdiameterSign}${Math.abs(boreDiameterModifier)
          .toFixed(2)
          .replace(/0*$/, "")}`;

  return (
    baffleResult +
    chamberResult +
    striationsResult +
    " " +
    tipOpening +
    facing +
    internalVolumeResult +
    boreDiameterResult
  );
}
